import React from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Heart,
  Users,
  ShieldAlert,
} from "lucide-react";
import { StaffDetails } from "../../../types/staff.types";

interface PersonalTabProps {
  details: StaffDetails | null;
  isEditing: boolean;
  editFormData: any;
  onChange: (field: string, value: any) => void;
}

export const splitFullName = (
  fullName?: string | null
): { firstName: string; lastName: string } => {
  if (!fullName) return { firstName: "", lastName: "" };
  const trimmed = fullName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }
  const lastName = parts.pop() || "";
  const firstName = parts.join(" ");
  return { firstName, lastName };
};

export const PersonalTab: React.FC<PersonalTabProps> = ({
  details,
  isEditing,
  editFormData,
  onChange,
}) => {
  const initialNameParts = splitFullName(editFormData.fullName);
  const [firstName, setFirstName] = React.useState(initialNameParts.firstName);
  const [lastName, setLastName] = React.useState(initialNameParts.lastName);

  React.useEffect(() => {
    const parts = splitFullName(editFormData.fullName);
    setFirstName(parts.firstName);
    setLastName(parts.lastName);
  }, [editFormData.fullName]);

  const handleNameChange = (newFirst: string, newLast: string) => {
    setFirstName(newFirst);
    setLastName(newLast);
    const combined = [newFirst.trim(), newLast.trim()].filter(Boolean).join(" ");
    onChange("fullName", combined);
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        {/* Section 1: Basic Identity & Bio */}
        <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-b border-gray-200/80 pb-2.5">
            <User className="w-4 h-4 text-emerald-600" />
            <span>Personal & Biological Information</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => handleNameChange(e.target.value, lastName)}
                placeholder="e.g. Ayush"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => handleNameChange(firstName, e.target.value)}
                placeholder="e.g. Sahu"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Father / Spouse Name
              </label>
              <input
                type="text"
                value={editFormData.fatherSpouseName || ""}
                onChange={(e) => onChange("fatherSpouseName", e.target.value)}
                placeholder="Father or spouse name"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Date of Birth
              </label>
              <input
                type="date"
                value={editFormData.dateOfBirth ? editFormData.dateOfBirth.split("T")[0] : ""}
                onChange={(e) => onChange("dateOfBirth", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Blood Group
              </label>
              <input
                type="text"
                value={editFormData.bloodGroup || ""}
                onChange={(e) => onChange("bloodGroup", e.target.value)}
                placeholder="e.g. O+, A+, B+"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Gender
              </label>
              <select
                value={editFormData.gender || ""}
                onChange={(e) => onChange("gender", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 capitalize"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section 2: Contact & Addresses */}
        <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-b border-gray-200/80 pb-2.5">
            <Phone className="w-4 h-4 text-emerald-600" />
            <span>Contact & Residential Details</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Primary Mobile <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={editFormData.mobileNumber || ""}
                onChange={(e) => onChange("mobileNumber", e.target.value)}
                placeholder="10-digit mobile number"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Alternate Mobile
              </label>
              <input
                type="tel"
                value={editFormData.alternateMobileNumber || ""}
                onChange={(e) => onChange("alternateMobileNumber", e.target.value)}
                placeholder="Secondary phone"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Personal Email
              </label>
              <input
                type="email"
                value={editFormData.personalEmail || ""}
                onChange={(e) => onChange("personalEmail", e.target.value)}
                placeholder="email@example.com"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Current Address
              </label>
              <textarea
                rows={2}
                value={editFormData.currentAddress || ""}
                onChange={(e) => onChange("currentAddress", e.target.value)}
                placeholder="Present residential address"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 resize-none"
              />
              <div className="mt-2">
                <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                  Current Pincode
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={editFormData.currentPincode || ""}
                  onChange={(e) => onChange("currentPincode", e.target.value)}
                  placeholder="6-digit pincode"
                  className="w-full px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-gray-700">
                  Permanent Address
                </label>
                <button
                  type="button"
                  onClick={() => {
                    onChange("permanentAddress", editFormData.currentAddress || "");
                    onChange("permanentPincode", editFormData.currentPincode || "");
                  }}
                  className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded-md border border-emerald-200 transition-colors inline-flex items-center gap-1 shadow-2xs"
                  title="Copy current address and pincode to permanent address"
                >
                  <span>Same as current</span>
                </button>
              </div>
              <textarea
                rows={2}
                value={editFormData.permanentAddress || ""}
                onChange={(e) => onChange("permanentAddress", e.target.value)}
                placeholder="Permanent home address"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 resize-none"
              />
              <div className="mt-2">
                <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                  Permanent Pincode
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={editFormData.permanentPincode || ""}
                  onChange={(e) => onChange("permanentPincode", e.target.value)}
                  placeholder="6-digit pincode"
                  className="w-full px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Emergency Contact */}
        <div className="bg-amber-50/40 border border-amber-200/60 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-amber-900 border-b border-amber-200/60 pb-2.5">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            <span>Emergency Contact Information</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Contact Person Name
              </label>
              <input
                type="text"
                value={editFormData.emergencyContactName || ""}
                onChange={(e) => onChange("emergencyContactName", e.target.value)}
                placeholder="e.g. Mary Doe"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Emergency Phone Number
              </label>
              <input
                type="tel"
                value={editFormData.emergencyContactNumber || ""}
                onChange={(e) => onChange("emergencyContactNumber", e.target.value)}
                placeholder="10-digit emergency phone"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Relationship
              </label>
              <input
                type="text"
                value={editFormData.emergencyContactRelation || ""}
                onChange={(e) => onChange("emergencyContactRelation", e.target.value)}
                placeholder="e.g. Spouse, Parent, Sibling"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // View Mode
  const { firstName: viewFirstName, lastName: viewLastName } = splitFullName(details?.fullName);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Personal Info */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-sm font-bold text-gray-900">
            <User className="w-4 h-4 text-emerald-600" />
            <span>Personal & Biological Information</span>
          </div>

          <div className="grid grid-cols-2 gap-y-3.5 text-xs">
            <div>
              <p className="text-gray-400 font-medium">First Name</p>
              <p className="text-gray-900 font-semibold text-sm mt-0.5">{viewFirstName || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Last Name</p>
              <p className="text-gray-800 font-medium text-sm mt-0.5">{viewLastName || "-"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Gender</p>
              <p className="text-gray-800 font-medium capitalize mt-0.5">{details?.gender || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Father / Spouse</p>
              <p className="text-gray-800 font-medium mt-0.5">{details?.fatherSpouseName || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Date of Birth</p>
              <p className="text-gray-800 font-medium mt-0.5">
                {details?.dateOfBirth ? new Date(details.dateOfBirth).toLocaleDateString("en-IN") : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Blood Group</p>
              <p className="text-gray-800 font-semibold mt-0.5">{details?.bloodGroup || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Card 2: Contact & Addresses */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-sm font-bold text-gray-900">
            <Phone className="w-4 h-4 text-emerald-600" />
            <span>Contact & Address Information</span>
          </div>

          <div className="grid grid-cols-2 gap-y-3.5 text-xs">
            <div>
              <p className="text-gray-400 font-medium">Primary Mobile</p>
              <p className="text-gray-900 font-semibold text-sm mt-0.5">{details?.mobileNumber || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-400 font-medium">Alternate Mobile</p>
              <p className="text-gray-800 font-medium mt-0.5">{details?.alternateMobileNumber || "N/A"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-400 font-medium">Personal Email</p>
              <p className="text-gray-800 font-medium mt-0.5">{details?.personalEmail || "N/A"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-400 font-medium">Current Address</p>
              <p className="text-gray-800 font-medium mt-0.5">
                {details?.currentAddress || "N/A"}
                {details?.currentPincode ? ` (Pincode: ${details.currentPincode})` : ""}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-400 font-medium">Permanent Address</p>
              <p className="text-gray-800 font-medium mt-0.5">
                {details?.permanentAddress || "N/A"}
                {details?.permanentPincode ? ` (Pincode: ${details.permanentPincode})` : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Card 3: Emergency Contact */}
      <div className="bg-amber-50/30 border border-amber-200/70 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-amber-200/50 text-sm font-bold text-amber-900">
          <ShieldAlert className="w-4 h-4 text-amber-600" />
          <span>Emergency Contact Person</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div>
            <p className="text-gray-500 font-medium">Contact Person</p>
            <p className="text-gray-900 font-semibold text-sm mt-0.5">{details?.emergencyContactName || "N/A"}</p>
          </div>
          <div>
            <p className="text-gray-500 font-medium">Emergency Number</p>
            <p className="text-gray-900 font-semibold text-sm mt-0.5">{details?.emergencyContactNumber || "N/A"}</p>
          </div>
          <div>
            <p className="text-gray-500 font-medium">Relationship</p>
            <p className="text-gray-800 font-medium mt-0.5">{details?.emergencyContactRelation || "N/A"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
